/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import { clsx } from 'clsx'

import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'

import { type Editor } from '@tiptap/core'
import { type Range } from '@tiptap/react'
import {
  VALID_IMAGE_MIME_TYPES,
  VALID_VIDEO_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { RichTextOption } from '../../../../../../../../../../../../packages/tiptap-api-utils/src/options'
import { FlagKeys } from '~/lib/flags/definitions'
import { useFlags } from '~/lib/flags/provider'

export type CommandCategory = {
  id: string
  name: string
  commands: Array<Command>
}

export type Command = {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  featureFlag?: FlagKeys
  isNew?: boolean
  preview?: { url: string; mimeType: string; width: number; height: number }
  keywords?: string[]
  tipTapKey?: RichTextOption
  command: (opts: {
    editor: Editor
    range: Range
    onExit: () => void
    onSelectBlock: SlashCommandType['onSelectBlock']
  }) => void
}

export type SlashCommandType = {
  component: React.FC<CommandListComponentProps> | null
  suggestion: {
    char: string
    startOfLine?: boolean
    pluginKey: string
  }
  onOpen: () => void
  onClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectBlock: (...args: any[]) => any
  richTextId: string
  isSingleLineBlock?: boolean
}

export type ControllerProps = {
  onExit: () => void
  items: CommandCategory[]
  command: (item: CommandCategory & { onExit: () => void }) => void
  component: React.FC<CommandListComponentProps>
  isOpen: boolean
}

export type CommandListComponentProps = {
  items: CommandCategory[]
  selectedIndex: number
  selectItem: (idx: number) => void
  setSelectedIndex: (idx: number) => void
  disabled?: boolean
  onExit: () => void
  setMouseInteracting: (value: boolean) => void
}

type CommandItemProps = {
  command: Command
  index: number
  isSelected: boolean
  disabled?: boolean
  isPointerIdle: boolean
  selectItem: (index: number) => void
  setSelectedIndex: (index: number) => void
  setSelectedCommand: (command: Command) => void
  setMouseInteracting: (value: boolean) => void
}

const CommandItem: React.FC<CommandItemProps> = ({
  command: c,
  index,
  isSelected,
  disabled,
  isPointerIdle,
  selectItem,
  setSelectedIndex,
  setSelectedCommand,
  setMouseInteracting,
}) => {
  React.useEffect(() => {
    if (isSelected) {
      setSelectedCommand(c)
    }
  }, [isSelected, c, setSelectedCommand])

  return (
    <li data-selected={isSelected} key={c.id + c.title} className="scroll-m-2">
      <button
        className={clsx(
          'flex w-full cursor-default items-center gap-3 rounded-md p-2 text-left',
          isSelected && 'bg-dark-shade'
        )}
        onMouseDown={() => {
          setMouseInteracting(true)
        }}
        onMouseUp={() => {
          setMouseInteracting(false)
        }}
        onClick={() => {
          if (disabled) return
          selectItem(index)
        }}
        onPointerEnter={() => {
          if (isPointerIdle) return
          setSelectedIndex(index)
        }}
      >
        <span
          className={clsx(
            isSelected && 'border-dark-elevated bg-dark-shade-hover',
            'flex h-[31px] w-[31px] shrink-0 items-center justify-center overflow-hidden rounded border border-dark-border-solid'
          )}
        >
          {c.icon}
        </span>
        <p className="flex flex-col gap-1">
          <span className="text-sm font-medium leading-4 text-dark-dim flex items-center gap-x-1.5">
            {c.title}
            {c.isNew && (
              <span className="text-[0.6875rem] font-semibold leading-4 text-dark-control-base bg-dark-control-base bg-opacity-15 px-1.5 rounded-full">
                NEW
              </span>
            )}
          </span>
          {c.description && (
            <span className="text-xs leading-4 text-dark-faint line-clamp-2">
              {c.description}
            </span>
          )}
        </p>
      </button>
    </li>
  )
}

export const List = ({
  items,
  selectedIndex,
  setSelectedIndex,
  selectItem,
  disabled,
  onExit,
  setMouseInteracting,
}: CommandListComponentProps) => {
  const listRef = React.useRef<HTMLUListElement>(null)
  const [isPointerIdle, setIsPointerIdle] = React.useState(true)

  const selectedIndexRef = React.useRef(selectedIndex)
  const itemsRef = React.useRef(items)

  React.useEffect(() => {
    selectedIndexRef.current = selectedIndex
  }, [selectedIndex])

  React.useEffect(() => {
    itemsRef.current = items
  }, [items])

  // ensure active item is in view
  React.useEffect(() => {
    if (!listRef.current) return
    if (!isPointerIdle) return
    const item = listRef.current.querySelector<HTMLLIElement>(
      '[data-selected="true"]'
    )
    if (!item) return
    setTimeout(() => {
      item.scrollIntoView({ block: 'nearest' })
    }, 100)
  }, [selectedIndex, isPointerIdle])

  React.useEffect(() => {
    const handleMouseMove = () => {
      setIsPointerIdle(false)
    }
    const handleKeyDown = () => {
      setIsPointerIdle(true)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  React.useEffect(() => {
    if (disabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp': {
          const newIndex = selectedIndexRef.current - 1

          if (newIndex < 0) {
            // do nothing
            break
          }

          setSelectedIndex(newIndex)
          break
        }
        case 'ArrowDown': {
          const flatItems = itemsRef.current.flatMap((c) => c.commands)
          const newIndex = selectedIndexRef.current + 1

          if (newIndex >= flatItems.length) {
            // do nothing
            break
          }

          setSelectedIndex(newIndex)
          break
        }
        case 'Enter': {
          selectItem(selectedIndexRef.current)
          break
        }
        case 'Escape': {
          onExit()
          break
        }

        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [disabled, onExit, selectItem, setSelectedIndex])

  const maxListHeight = 324
  const placeholderHeight = 8
  const [renderPlaceholder, setRenderPlaceholder] = React.useState(true)

  React.useEffect(() => {
    if (!listRef.current) return
    const currentListHeight = listRef.current.offsetHeight
    if (currentListHeight === 0) return // not ready yet
    if (currentListHeight + placeholderHeight <= maxListHeight) {
      // we won't have scroll, and so we remove the placeholder
      setRenderPlaceholder(false)
    } else {
      setRenderPlaceholder(true)
    }
  }, [items, renderPlaceholder])

  const [selectedCommand, setSelectedCommand] = React.useState<
    Command | undefined
  >()

  const flags = useFlags()

  return (
    <div className="relative" data-slash-command-enabled={!disabled}>
      <ScrollArea
        className="rounded-xl border border-dark-border-solid bg-dark-base shadow-modal"
        variant="subtle"
      >
        <ul
          ref={listRef}
          className="w-[332px] py-2 px-2"
          style={{ maxHeight: maxListHeight }}
        >
          {items.map((item, idx) => {
            const prevItemsCommandsLength = items
              .slice(0, idx)
              .flatMap((c) => c.commands).length

            return (
              <React.Fragment key={item.id}>
                {item.name && (
                  <p className="mt-4 mb-1 px-2.5 text-xs text-dark-muted first:mt-0 font-medium">
                    {item.name}
                  </p>
                )}
                {item.commands.map((c, itemIdx) => {
                  const adjustedIndex = prevItemsCommandsLength + itemIdx
                  const isSelected = adjustedIndex === selectedIndex

                  if (c.featureFlag && !flags[c.featureFlag]) return null
                  return (
                    <CommandItem
                      key={c.id + c.title}
                      command={c}
                      index={adjustedIndex}
                      disabled={disabled}
                      isSelected={isSelected}
                      isPointerIdle={isPointerIdle}
                      selectItem={selectItem}
                      setSelectedIndex={setSelectedIndex}
                      setSelectedCommand={setSelectedCommand}
                      setMouseInteracting={setMouseInteracting}
                    />
                  )
                })}
              </React.Fragment>
            )
          })}
          {items.length === 0 && (
            <p className="py-1 text-xs text-dark-muted">No results</p>
          )}
          {/* placeholder for scroll bottom */}
          <div
            style={{
              height: placeholderHeight,
              display: renderPlaceholder ? 'block' : 'none',
            }}
          />
        </ul>
      </ScrollArea>
      {selectedCommand && selectedCommand.preview && (
        <div className="rounded-xl overflow-hidden border border-dark-border-solid bg-dark-shade shadow-[0px_14px_16px_rgba(0,0,0,0.3)] flex items-center absolute left-[calc(100%+8px)] w-[364px] top-0">
          <>
            {VALID_IMAGE_MIME_TYPES.includes(
              selectedCommand.preview.mimeType
            ) && (
              <img
                src={selectedCommand.preview.url}
                alt=""
                className="w-full inset-0 object-cover"
                style={{
                  maxHeight: maxListHeight,
                  aspectRatio: `${selectedCommand.preview.width}/${selectedCommand.preview.height}`,
                }}
              />
            )}
            {VALID_VIDEO_MIME_TYPES.includes(
              selectedCommand.preview.mimeType
            ) && (
              <video
                src={selectedCommand.preview.url}
                className="w-full inset-0 object-cover"
                style={{
                  maxHeight: maxListHeight,
                  aspectRatio: `${selectedCommand.preview.width}/${selectedCommand.preview.height}`,
                }}
                autoPlay
                muted
                playsInline
                loop
              />
            )}
          </>
        </div>
      )}
    </div>
  )
}
